import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { CalendarDays, Sparkles, Clock } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Agenda = () => {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["agenda"],
    queryFn: async () => (await axios.get(`${API}/agenda`)).data,
    staleTime: 10 * 60 * 1000,
  });

  const rutin = items.filter((i) => i.category === "rutin").sort((a, b) => a.order - b.order);
  const tahunan = items.filter((i) => i.category === "tahunan").sort((a, b) => a.order - b.order);

  return (
    <section id="agenda" className="relative bg-sand-100 py-28 lg:py-36" data-testid="agenda-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <SectionHeading
          eyebrow="Agenda Kegiatan"
          title={
            <>
              Kajian rutin & <em>acara</em> mendatang.
            </>
          }
          description="Masjid Pantai Bali hidup sepanjang tahun—dari kajian harian hingga perhelatan budaya pesisir. Berikut kegiatan yang bisa Anda ikuti."
        />

        {isLoading ? (
          <div className="mt-16 grid gap-4 lg:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-40 animate-pulse rounded-3xl border border-navy/10 bg-white/60" />
            ))}
          </div>
        ) : (
          <div className="mt-16 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="mb-6 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-navy text-gold">
                  <Clock className="h-5 w-5" />
                </span>
                <h3 className="font-display text-2xl text-navy">Kegiatan Rutin</h3>
              </div>
              <div className="overflow-hidden rounded-[2rem] border border-navy/10 bg-white" data-testid="agenda-rutin-list">
                {rutin.map((it, i) => (
                  <Reveal key={it.id} delay={i * 0.06}>
                    <div className={`flex flex-col gap-1 p-6 sm:flex-row sm:items-center sm:justify-between sm:gap-6 ${i !== 0 ? "border-t border-navy/10" : ""}`} data-testid={`agenda-rutin-${it.id}`}>
                      <div className="sm:max-w-md">
                        <h4 className="font-display text-xl text-navy">{it.title}</h4>
                        {it.description && <p className="mt-1 text-sm text-slate-600">{it.description}</p>}
                      </div>
                      <span className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-sand-100 px-4 py-2 text-xs font-semibold text-teal sm:self-center">
                        <CalendarDays className="h-3.5 w-3.5" /> {it.when}
                      </span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="mb-6 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-gold text-navy">
                  <Sparkles className="h-5 w-5" />
                </span>
                <h3 className="font-display text-2xl text-navy">Agenda Tahunan</h3>
              </div>
              <div className="space-y-4" data-testid="agenda-tahunan-list">
                {tahunan.map((it, i) => (
                  <Reveal key={it.id} delay={i * 0.08}>
                    <article className="dark-surface rounded-[2rem] bg-navy p-7 text-sand-50" data-testid={`agenda-tahunan-${it.id}`}>
                      <span className="eyebrow inline-flex items-center gap-2 rounded-full bg-gold px-3 py-1.5 text-navy">
                        <CalendarDays className="h-3.5 w-3.5" /> {it.when}
                      </span>
                      <h4 className="mt-4 font-display text-3xl leading-tight">{it.title}</h4>
                      {it.description && <p className="mt-2 text-sm text-sand-50/70">{it.description}</p>}
                      {it.highlights?.length > 0 && (
                        <ul className="mt-5 flex flex-wrap gap-2">
                          {it.highlights.map((h) => (
                            <li key={h} className="rounded-full border border-sand-50/20 px-3 py-1.5 text-xs text-sand-50/90">
                              {h}
                            </li>
                          ))}
                        </ul>
                      )}
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
